// Lightweight runtime diagnostics to surface main-thread stalls.
//
// For parity work we want a quiet console + minimal overhead by default.
// Enable explicitly via:
// - window.__FG_DIAG = true
// - or window.__FG_PERF = true
// - or URL param `?perf=1`

let installed = false;

function longTaskObserver(thresholdMs: number) {
    if (typeof PerformanceObserver === 'undefined') return;
    try {
        const observer = new PerformanceObserver((list) => {
            for (const entry of list.getEntries()) {
                const duration = entry.duration;
                if (duration < thresholdMs) continue;
                // @ts-ignore attribution is experimental
                const attribution = entry.attribution || [];
                const attrStr = attribution.map((a: any) => {
                    const name = a.name || a.containerName || '';
                    const src = a.containerSrc || a.scriptUrl || '';
                    const id = a.containerId ? `#${a.containerId}` : '';
                    return `${name}${id}${src ? `@${src}` : ''}`;
                }).join(', ');
                const attrDetail = attribution.map((a: any) => JSON.stringify({
                    name: a.name,
                    containerName: a.containerName,
                    containerSrc: a.containerSrc,
                    containerId: a.containerId,
                    scriptId: a.scriptId,
                    scriptUrl: a.scriptUrl,
                })).join(' | ');
                try {
                    // eslint-disable-next-line no-console
                    console.log('[FG-LONGTASK]', JSON.stringify({
                        duration_ms: Number(duration.toFixed(1)),
                        start_ms: Number(entry.startTime.toFixed(1)),
                        attrs: attrStr || null,
                        detail: attrDetail || null,
                    }));
                } catch {
                    // eslint-disable-next-line no-console
                    console.log(
                        `[FG-LONGTASK] ${duration.toFixed(1)}ms @${entry.startTime.toFixed(1)}ms` +
                        (attrStr ? ` attrs: ${attrStr}` : '') +
                        (attrDetail ? ` detail: ${attrDetail}` : '')
                    );
                }
            }
        });
        observer.observe({ entryTypes: ['longtask'] });
    } catch (_e) {
        // Ignore if not supported.
    }
}

function eventLoopLagLogger(thresholdMs: number, sampleMs: number) {
    let last = performance.now();
    setInterval(() => {
        const now = performance.now();
        const drift = now - last - sampleMs;
        if (drift > thresholdMs) {
            // eslint-disable-next-line no-console
            console.log(`[FG-LOOPTICK] lag ${drift.toFixed(1)}ms (interval ${sampleMs}ms)`);
        }
        last = now;
    }, sampleMs);
}

function frameDropLogger(thresholdMs: number) {
    let last = performance.now();
    const tick = () => {
        const now = performance.now();
        const dt = now - last;
        if (dt > thresholdMs) {
            // eslint-disable-next-line no-console
            console.log(`[FG-RAFDROP] gap ${dt.toFixed(1)}ms`);
        }
        last = now;
        requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
}

function eventTimingLogger(thresholdMs: number) {
    if (typeof PerformanceObserver === 'undefined') return;
    try {
        const observer = new PerformanceObserver((list) => {
            for (const entry of list.getEntries() as PerformanceEventTiming[]) {
                const dur = entry.duration;
                if (dur < thresholdMs) continue;
                const name = entry.name;
                const start = entry.startTime.toFixed(1);
                const processing = (entry.processingEnd - entry.processingStart).toFixed(1);
                const interaction = (entry as any).interactionId ?? '';
                // eslint-disable-next-line no-console
                console.log(`[FG-EVENT] ${name} duration ${dur.toFixed(1)}ms processing ${processing}ms @${start}ms interaction:${interaction}`);
            }
        });
        // durationThreshold is not typed in TS lib yet; cast to any to enable filtering.
        observer.observe({ type: 'event', buffered: true, durationThreshold: thresholdMs } as any);
    } catch (_e) {
        // Ignore if not supported.
    }
}

export function installDiagnostics() {
    if (installed) return;
    if (typeof window === 'undefined') return;

    const diagFlag = (window as any).__FG_DIAG;
    const perfFlag = (window as any).__FG_PERF;
    const urlPerf = (() => {
        try {
            const sp = new URLSearchParams(window.location.search)
            const perf = String(sp.get('perf') || '').trim().toLowerCase()
            return perf === '1' || perf === 'true' || perf === 'yes'
        } catch {
            return false
        }
    })()
    const eventDiagEnabled = (() => {
        try {
            const w = window as any
            if (w.__FG_DIAG_EVENTS === true) return true
            const sp = new URLSearchParams(window.location.search)
            const raw = String(sp.get('fg_diag_events') || '').trim().toLowerCase()
            return raw === '1' || raw === 'true' || raw === 'yes' || raw === 'on'
        } catch {
            return false
        }
    })()

    // Default OFF unless explicitly enabled.
    if (perfFlag !== true && diagFlag !== true && !urlPerf) return;

    installed = true;
    // eslint-disable-next-line no-console
    console.log('[FG-DIAG] diagnostics enabled (longtask/looptick/rafdrop)');
    longTaskObserver(50);
    eventLoopLagLogger(50, 50);
    frameDropLogger(50);
    if (eventDiagEnabled) eventTimingLogger(50);
}

